import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { useIntl } from 'react-intl';
import useQuery from '../../../../../modules/react/hooks/useQuery';
import useCurrentBuilderPage from '../../../../../admin/hooks/useCurrentBuilderPage';
import widgetDecorator from './widget';

export const GET_MEDIA = gql`
  query GetMedia($ids: [ID!], $filenames: [String!], $excludeTrash: Boolean, $locale: String) {
    media: mediaByIds(ids: $ids, filenames: $filenames, excludeTrash: $excludeTrash) {
      id
      filename
      url
      placeholder
      srcSet
      sourceWidth
      sourceHeight
      trash
      defaultMetadata {
        title
        altText
      }
      metadata(locale: $locale) {
        title
        altText
      }
    }
  }
`;

export default (details, { resolve = null, ...options } = {}) => {
  const createHoc = (Component) => {
    const WidgetComponent = widgetDecorator(details, { ...options, resolve })(Component);

    const ImageWidget = (props) => {
      const { locale: builderPageLocale } = useCurrentBuilderPage();
      const { locale: intlProviderLocale } = useIntl();
      const locale = builderPageLocale || intlProviderLocale;
      const { companyId, image, images, otherImages, heroImages, ...rest } = props;

      let singleImg;
      if (image) {
        singleImg = [image];
      }

      let pulledImages = singleImg || images || otherImages || heroImages;


      if (!pulledImages) {
        const newProps = {
          images: [],
          ...rest,
        };
        return <WidgetComponent {...newProps} />;
      }

      /*
        Images uploaded before the Media Gallery launch will have a `file` key.
        This `file` key will take the form of `${companyId}/name`.
        These images do not have the `companyId` prepended to the filename,
        so, to find all Medium documents by ID or filename, we want to clean up this
        `file` key to remove the `companyId` prepended to it

         The alternative to this would be to write a migration to replace all `file`s in
         the widgets payload with its accompanying `mediumId`. If you're brave enough to update
         image widget payloads across 30k+ pages to reduce the complexity of this decorator...have fun?
      */
      pulledImages = pulledImages.filter(Boolean).map((img) => {
        if (img && img.file) {
          return {
            ...img,
            staticImg: !img.file.startsWith(companyId),
            filename: img.file.replace(`${companyId}/`, ''),
          };
        }
        return {
          ...img,
          staticImg: false,
        };
      });

      const { ids: mediaIds, filenames } = pulledImages.reduce((acc, img) => {
        const { ids: accIds, filenames: accFilenames } = acc;
        if (img && img.mediumId) {
          return ({
            ids: [...accIds, img.mediumId],
            filenames: accFilenames,
          });
        }

        if (img && img.file) {
          return ({
            ids: accIds,
            filenames: [...accFilenames, img.filename],
          });
        }

        return acc;
      }, { ids: [], filenames: [] });

      const { data } = useQuery(
        GET_MEDIA,
        { variables: { ids: mediaIds, filenames, excludeTrash: !props.isBuilderView, locale } },
      );

      /*
         Given a set of non-null assets from a widget and a set of Medium documents,
         we want to create the union of widget assets and Medium documents.

         For every asset, we want to add a `medium` key, where its value is the Medium document where
         `asset.id` or `asset.filename` matches either the `id` or `filename` in our set of Medium documents.

         If a Medium document cannot be found and it is either a videoEmbedCode or a staticImage, include the asset.
         If a Medium document can be found, but it is soft deleted, do not include the asset.
       */
      let imagesToDisplay = [];
      if (data && data.media.length > 0) {
        const { media } = data;

        const mediaMap = new Map();
        media.forEach((medium) => {
          mediaMap.set(medium.id, medium);
          mediaMap.set(medium.filename, medium);
          return medium;
        });

        imagesToDisplay = pulledImages.reduce((acc, val) => {
          if (!val) { return acc; }

          if (val.videoEmbedCode || val.staticImg) {
            return [
              ...acc,
              {
                ...val,
              },
            ];
          }

          const foundMedium = mediaMap.get(val.mediumId || val.filename);
          if (foundMedium && !foundMedium.trash) {
            return [
              ...acc,
              {
                ...val,
                medium: {
                  ...foundMedium,
                },
              },
            ];
          }

          // asset is either soft or hard deleted, do not pass it through to FE
          if ((foundMedium && foundMedium.trash) || (!foundMedium && !val.staticImg)) {
            return acc;
          }

          return [
            ...acc,
            {
              ...val,
            },
          ];
        }, []);
      } else {
        const newProps = {
          images: pulledImages || [],
          ...rest,
        };
        return <WidgetComponent {...newProps} />;
      }

      const newProps = {
        images: imagesToDisplay,
        ...rest,
      };

      return <WidgetComponent {...newProps} />;
    };

    const ImageType = PropTypes.shape({
      file: PropTypes.string,
      filename: PropTypes.string,
      alt: PropTypes.string,
      link: PropTypes.string,
      mediumId: PropTypes.string,
      target: PropTypes.string,
      title: PropTypes.string,
      staticImg: PropTypes.bool,
    });

    ImageWidget.propTypes = {
      companyId: PropTypes.string.isRequired,
      images: PropTypes.arrayOf(ImageType),
      image: ImageType,
      isBuilderView: PropTypes.bool,
      otherImages: PropTypes.arrayOf(ImageType),
      heroImages: PropTypes.arrayOf(ImageType),
      mediaGalleryEnabled: PropTypes.bool,
    };

    ImageWidget.defaultProps = {
      images: undefined,
      image: undefined,
      otherImages: undefined,
      heroImages: undefined,
      isBuilderView: false,
      mediaGalleryEnabled: true,
    };

    ImageWidget.displayName = `ImageWidget(${WidgetComponent.displayName})`;

    return connect()(hoistNonReactStatic(ImageWidget, WidgetComponent));
  };

  if (process.env.NODE_ENV === 'testing') {
    createHoc.$$voyagerTestWhitelist = true;
  }

  return createHoc;
};
